import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { Download, Link2, Maximize2, Redo2, RotateCcw, Type, Undo2, ZoomIn, ZoomOut, Eraser } from 'lucide-react';
import { useStrings } from '../locales';
import { Bounds, KohdLayout, KohdMoves, layoutKohd, Pt } from '../kohd/layout';
import { applyEdits, KohdEdits, TraceEdit } from '../kohd/edits';
import { kohdSvg } from '../kohd/svg';
import KohdCanvas, { View } from '../kohd/react/KohdCanvas';
import KohdLegend from '../components/KohdLegend';
import './Kohd.css';

interface KohdDoc {
  edits: KohdEdits;
  moves: KohdMoves;
}

const STORAGE_KEY = 'dhtools-kohd';
const SAMPLE = 'The network carries communication from city to city.';
const EMPTY_DOC: KohdDoc = { edits: {}, moves: {} };
const isRecord = (v: unknown): v is Record<string, never> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** Edits saved before trace ends could move were bare bend arrays. */
const upgradeEdits = (edits: Record<string, unknown>): KohdEdits =>
  Object.fromEntries(Object.entries(edits).map(([k, v]) => [k, Array.isArray(v) ? { bends: v } : v])) as KohdEdits;

/** A shared ?t= link wins over the text saved on this device. */
function loadInitial(): KohdDoc & { text: string } {
  const shared = new URLSearchParams(window.location.search).get('t');
  if (shared !== null) return { text: shared, ...EMPTY_DOC };
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
    return {
      text: typeof saved.text === 'string' ? saved.text : SAMPLE,
      edits: isRecord(saved.edits) ? upgradeEdits(saved.edits) : {},
      moves: isRecord(saved.moves) ? saved.moves : {},
    };
  } catch {
    return { text: SAMPLE, ...EMPTY_DOC };
  }
}

const fitView = (b: Bounds): View => ({ x: b.minX, y: b.minY, w: b.maxX - b.minX, h: b.maxY - b.minY });
const without = <T,>(record: Record<string, T>, key: string) => Object.fromEntries(Object.entries(record).filter(([k]) => k !== key));
const fileName = (text: string, ext: string) => `kohd-${text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40) || 'circuit'}.${ext}`;

function download(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

/** Standalone SVG of the whole circuit in the colors currently on screen. */
function screenSvg(svg: SVGSVGElement, layout: KohdLayout, labels: boolean): string {
  const style = getComputedStyle(svg);
  return kohdSvg(layout, { ink: style.color, paper: style.backgroundColor, labels });
}

export default function Kohd() {
  const s = useStrings().kohd;
  const [initial] = useState(loadInitial);
  const [text, setText] = useState(initial.text);
  const [doc, setDoc] = useState<KohdDoc>({ edits: initial.edits, moves: initial.moves });
  const [past, setPast] = useState<KohdDoc[]>([]);
  const [future, setFuture] = useState<KohdDoc[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [manualView, setManualView] = useState<View | null>(null);
  const [showLabels, setShowLabels] = useState(true);
  const [note, setNote] = useState('');
  const svgRef = useRef<SVGSVGElement>(null);

  const deferredText = useDeferredValue(text);
  const base = useMemo(() => layoutKohd(deferredText, doc.moves), [deferredText, doc.moves]);
  const layout = useMemo(() => applyEdits(base, doc.edits), [base, doc.edits]);
  const view = manualView ?? fitView(layout.bounds);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ text, ...doc })); } catch { /* storage blocked */ }
  }, [text, doc]);

  const beginEdit = useCallback(() => {
    setPast((p) => [...p.slice(-99), doc]);
    setFuture([]);
  }, [doc]);

  const undo = useCallback(() => {
    if (past.length === 0) return;
    setFuture((f) => [doc, ...f]);
    setDoc(past[past.length - 1]);
    setPast((p) => p.slice(0, -1));
  }, [past, doc]);

  const redo = useCallback(() => {
    if (future.length === 0) return;
    setPast((p) => [...p, doc]);
    setDoc(future[0]);
    setFuture((f) => f.slice(1));
  }, [future, doc]);

  const hasEdit = selected !== null && (selected in doc.edits || selected in doc.moves);
  const resetSelected = () => {
    if (!selected || !hasEdit) return;
    beginEdit();
    setDoc((d) => ({ edits: without(d.edits, selected), moves: without(d.moves, selected) }));
  };
  const resetAll = () => {
    beginEdit();
    setDoc(EMPTY_DOC);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).closest('textarea, input')) return; // keep native text undo
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo(); else undo();
      } else if (mod && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        redo();
      } else if (e.key === 'Escape') {
        setSelected(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  const onEdit = useCallback((key: string, edit: TraceEdit) => {
    setDoc((d) => ({ ...d, edits: { ...d.edits, [key]: { ...d.edits[key], ...edit } } }));
  }, []);

  const onMoveWord = useCallback((key: string, delta: Pt) => {
    setDoc((d) => {
      const m = d.moves[key] ?? { x: 0, y: 0 };
      return { ...d, moves: { ...d.moves, [key]: { x: m.x + delta.x, y: m.y + delta.y } } };
    });
  }, []);

  const zoom = (k: number) => {
    const cx = view.x + view.w / 2;
    const cy = view.y + view.h / 2;
    setManualView({ x: cx - (view.w * k) / 2, y: cy - (view.h * k) / 2, w: view.w * k, h: view.h * k });
  };

  const exportSvg = () => {
    if (!svgRef.current) return;
    download(new Blob([screenSvg(svgRef.current, layout, showLabels)], { type: 'image/svg+xml' }), fileName(text, 'svg'));
  };

  const exportPng = () => {
    if (!svgRef.current) return;
    const markup = screenSvg(svgRef.current, layout, showLabels);
    const width = Math.ceil(layout.bounds.maxX - layout.bounds.minX);
    const height = Math.ceil(layout.bounds.maxY - layout.bounds.minY);
    const scale = Math.min(2, 8000 / Math.max(width, height)); // stay under browser canvas limits
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(width * scale);
      canvas.height = Math.round(height * scale);
      canvas.getContext('2d')?.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => blob && download(blob, fileName(text, 'png')));
    };
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;
  };

  const copyLink = () => {
    const url = `${window.location.origin}${window.location.pathname}?t=${encodeURIComponent(text)}`;
    if (!navigator.clipboard) { setNote(s.copyFailed); return; }
    navigator.clipboard.writeText(url).then(() => setNote(s.linkCopied), () => setNote(s.copyFailed));
  };

  const words = layout.rows.reduce((n, r) => n + r.words.length, 0);
  const nodes = layout.rows.reduce((n, r) => n + r.words.reduce((m, w) => m + w.nodes.length, 0), 0);
  const traces = layout.rows.reduce((n, r) => n + r.links.length + r.words.reduce((m, w) => m + w.traces.length, 0), 0);
  const readout: Array<[string, number]> = [[s.statWords, words], [s.statNodes, nodes], [s.statTraces, traces]];
  const zoomPct = Math.round((fitView(layout.bounds).w / view.w) * 100);

  return (
    <div className="page-content kohd-page">
      <div className="page-header kohd-header">
        <p className="kohd-kicker">{s.kicker}</p>
        <h1>{s.title}</h1>
        <p>{s.desc}</p>
      </div>

      <div className="kohd-layout">
        <div className="kohd-stage" style={{ aspectRatio: `${layout.bounds.maxX - layout.bounds.minX} / ${layout.bounds.maxY - layout.bounds.minY}` }}>
          <KohdCanvas
            layout={layout}
            view={view}
            onView={setManualView}
            selected={selected}
            onSelect={setSelected}
            onBeginEdit={beginEdit}
            onEdit={onEdit}
            onMoveWord={onMoveWord}
            showLabels={showLabels}
            svgRef={svgRef}
          />
          <div className="kohd-hud" aria-hidden="true">{s.zoom} {zoomPct}%</div>
        </div>

        <section className="kohd-console" aria-label={s.consoleLabel}>
          <div className="kohd-console__bar">
            <span className="kohd-console__lights" aria-hidden="true"><i /><i /><i /></span>
            <span className="kohd-console__path" aria-hidden="true">{s.consolePath}</span>
            <dl className="kohd-readout">
              {readout.map(([label, value]) => (
                <div key={label}><dt>{label}</dt><dd>{String(value).padStart(2, '0')}</dd></div>
              ))}
            </dl>
          </div>
          <div className="kohd-console__body">
            <div className="kohd-console__input">
              <label htmlFor="kohd-text" className="kohd-field-label">{s.inputLabel}</label>
              <div className="kohd-prompt">
                <span className="kohd-prompt__caret" aria-hidden="true">&gt;</span>
                <textarea
                  id="kohd-text"
                  className="search-input kohd-input"
                  value={text}
                  placeholder={s.placeholder}
                  onChange={(e) => setText(e.target.value)}
                  rows={2}
                  spellCheck={false}
                />
              </div>
            </div>

            <div className="kohd-console__controls">
              <div className="kohd-group" role="group" aria-label={s.edit}>
                <span className="kohd-group-label" aria-hidden="true">{s.edit}</span>
                <button type="button" className="filter-btn kohd-btn" onClick={undo} disabled={past.length === 0} title={s.undo}><Undo2 size={15} aria-hidden="true" />{s.undo}</button>
                <button type="button" className="filter-btn kohd-btn" onClick={redo} disabled={future.length === 0} title={s.redo}><Redo2 size={15} aria-hidden="true" />{s.redo}</button>
                <button type="button" className="filter-btn kohd-btn" onClick={resetSelected} disabled={!hasEdit}><RotateCcw size={15} aria-hidden="true" />{s.resetSelected}</button>
                <button type="button" className="filter-btn kohd-btn" onClick={resetAll} disabled={Object.keys(doc.edits).length + Object.keys(doc.moves).length === 0}><Eraser size={15} aria-hidden="true" />{s.resetAll}</button>
              </div>
    
              <div className="kohd-group" role="group" aria-label={s.view}>
                <span className="kohd-group-label" aria-hidden="true">{s.view}</span>
                <button type="button" className="filter-btn kohd-btn kohd-btn--icon" onClick={() => zoom(0.8)} aria-label={s.zoomIn} title={s.zoomIn}><ZoomIn size={16} aria-hidden="true" /></button>
                <button type="button" className="filter-btn kohd-btn kohd-btn--icon" onClick={() => zoom(1.25)} aria-label={s.zoomOut} title={s.zoomOut}><ZoomOut size={16} aria-hidden="true" /></button>
                <button type="button" className="filter-btn kohd-btn kohd-btn--icon" onClick={() => setManualView(null)} aria-label={s.fit} title={s.fit}><Maximize2 size={16} aria-hidden="true" /></button>
                <button type="button" className={`filter-btn kohd-btn${showLabels ? ' active' : ''}`} onClick={() => setShowLabels((v) => !v)} aria-pressed={showLabels}><Type size={15} aria-hidden="true" />{s.labels}</button>
              </div>
    
              <div className="kohd-group" role="group" aria-label={s.share}>
                <span className="kohd-group-label" aria-hidden="true">{s.share}</span>
                <button type="button" className="filter-btn kohd-btn" onClick={exportSvg}><Download size={15} aria-hidden="true" />{s.exportSvg}</button>
                <button type="button" className="filter-btn kohd-btn" onClick={exportPng}><Download size={15} aria-hidden="true" />{s.exportPng}</button>
                <button type="button" className="filter-btn kohd-btn" onClick={copyLink}><Link2 size={15} aria-hidden="true" />{s.copyLink}</button>
              </div>
            </div>
          </div>
          <div className="kohd-console__log">
            <p className="kohd-note" aria-live="polite">{note}</p>
            <p className="kohd-hint">{s.hint}</p>
          </div>
        </section>
      </div>

      <KohdLegend />
    </div>
  );
}
